import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Rocket, Award, Linkedin } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { founders, mentors, visionMission, advisoryBoard } from '../data/mock';
import { getOptimizedImageUrl } from '../lib/utils';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="about-page">
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden bg-gradient-to-br from-cream via-pragmatic/10 to-richolive/5">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pragmatic/15 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-richolive/10 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
                About Us
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-deepwalnut mb-6">
                Building India’s Next Generation of
                <span className="text-richolive"> Global Tech Leaders</span>
              </h1>
              <p className="text-xl text-heavyrain leading-relaxed">
                India’s official partner to FIRST Global since 2017, mentoring students to represent the nation on the world robotics stage.
              </p>
            </div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="py-20 bg-cream-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Vision Card */}
              <div className="bg-gradient-to-br from-pragmatic/20 to-cream border border-dwarfpony/30 rounded-2xl p-8 hover:shadow-lg transition-all">
                <div className="w-14 h-14 rounded-xl bg-pragmatic/30 flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-dwarfpony" />
                </div>
                <h2 className="text-2xl font-bold text-deepwalnut mb-4">Our Vision</h2>
                <p className="text-heavyrain text-lg leading-relaxed">{visionMission.vision}</p>
              </div>

              {/* Mission Card */}
              <div className="bg-gradient-to-br from-richolive/15 to-cream border border-richolive/30 rounded-2xl p-8 hover:shadow-lg transition-all">
                <div className="w-14 h-14 rounded-xl bg-richolive/20 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-richolive" />
                </div>
                <h2 className="text-2xl font-bold text-deepwalnut mb-4">Our Mission</h2>
                <p className="text-heavyrain text-lg leading-relaxed">{visionMission.mission}</p>
              </div>
            </div>

            {/* Goals */}
            <div className="bg-cream border border-pragmatic/30 rounded-2xl p-8 shadow-md">
              <h3 className="text-xl font-bold text-deepwalnut mb-6 text-center">Vision 2030 Goals</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {visionMission.goals.map((goal, index) => (
                  <div key={index} className="flex items-start gap-3 bg-gradient-to-r from-pragmatic/20 to-richolive/10 rounded-xl p-4 border border-pragmatic/20">
                    <Rocket className="w-5 h-5 text-dwarfpony mt-0.5 flex-shrink-0" />
                    <p className="text-deepwalnut text-sm">{goal}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Founders */}
        <section className="py-20 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
                Leadership
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-deepwalnut">
                Our <span className="text-richolive">Founders</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {founders.map((founder) => (
                <div
                  key={founder.id}
                  className="bg-cream border border-pragmatic/30 rounded-2xl p-8 hover:border-dwarfpony hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-6">
                    <img
                      src={getOptimizedImageUrl(founder.image, 400)}
                      srcSet={`
                        ${getOptimizedImageUrl(founder.image, 200)} 200w,
                        ${getOptimizedImageUrl(founder.image, 400)} 400w
                      `}
                      sizes="100px"
                      alt={founder.name}
                      className="w-24 h-24 rounded-xl object-cover"
                      loading="lazy"
                    />
                    <div className="flex-1">
                      <h3 className="text-deepwalnut font-bold text-xl">{founder.name}</h3>
                      <p className="text-dwarfpony text-sm mt-1">{founder.title}</p>
                      <a
                        href={founder.linkedIn}
                        className="inline-flex items-center gap-2 text-richolive hover:text-dwarfpony text-sm mt-3 transition-colors"
                      >
                        <Linkedin size={16} />
                        LinkedIn Profile
                      </a>
                    </div>
                  </div>
                  <ul className="mt-6 space-y-2">
                    {founder.achievements.map((achievement, i) => (
                      <li key={i} className="flex items-start gap-3 text-heavyrain text-sm">
                        <Award className="w-4 h-4 text-dwarfpony mt-0.5 flex-shrink-0" />
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team / Mentors */}
        <section className="py-20 bg-cream-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
                Our Team
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-deepwalnut mb-4">
                Mentors & <span className="text-richolive">Leaders</span>
              </h2>
              <p className="text-heavyrain max-w-2xl mx-auto">
                Guided by Leaders from Government, Industry & Academia.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {mentors.map((mentor) => (
                <div
                  key={mentor.id}
                  className="bg-cream border border-pragmatic/30 rounded-xl p-6 text-center hover:border-dwarfpony hover:shadow-md transition-all group"
                >
                  <img
                    src={getOptimizedImageUrl(mentor.image, 200)}
                    alt={mentor.name}
                    className="w-20 h-20 rounded-full object-cover mx-auto border-2 border-transparent group-hover:border-dwarfpony transition-colors"
                    loading="lazy"
                  />
                  <h3 className="text-deepwalnut font-semibold mt-4">{mentor.name}</h3>
                  <p className="text-heavyrain text-sm mt-1">{mentor.role}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-heavyrain mt-8">
              + 100 more industry experts and academic mentors
            </p>
          </div>
        </section>

        {/* Advisory Board */}
        <section className="py-20 bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
                Guidance
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-deepwalnut">
                Advisory <span className="text-richolive">Board</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {advisoryBoard.map((advisor, index) => (
                <div
                  key={index}
                  className="bg-cream border border-pragmatic/30 rounded-xl p-6 text-center hover:border-dwarfpony hover:shadow-md transition-all"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pragmatic/30 to-richolive/20 mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl font-bold text-deepwalnut">{advisor.name.charAt(0)}</span>
                  </div>
                  <h3 className="text-deepwalnut font-semibold">{advisor.name}</h3>
                  <p className="text-heavyrain text-sm mt-1">{advisor.role}</p>
                  <a
                    href={advisor.linkedIn}
                    className="inline-flex items-center gap-1 text-richolive hover:text-dwarfpony text-sm mt-3 transition-colors"
                  >
                    <Linkedin size={14} />
                    Connect
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-br from-midnight to-richolive-dark">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Shape the Future?
            </h2>
            <p className="text-heavyrain text-lg mb-8">
              Join us in our mission to make STEM education accessible to every student in India.
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-dwarfpony/25 group">
                Apply Now
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
